/**
 * CompactToolbar — shared icon-first list controls (DS EDIT 5aug26).
 *
 * Mobile screens were drowning in full-width search inputs and labeled sort
 * dropdowns. Per operator markup, every list surface collapses those to
 * icon-only controls that sit on ONE row:
 *  - IconSearch: a magnifier button that expands in-row to a real input
 *    (auto-expands while a query is active, collapses on blur when empty).
 *  - IconSelectMenu: an icon trigger + dropdown radio list replacing labeled
 *    <Select> sort/filter triggers; shows a primary dot when a non-default
 *    value is active.
 */
import { useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowUpDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface IconSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** data-testid for the input (kept stable with the pre-compact inputs). */
  testId?: string;
  className?: string;
  /** Extra control rendered inside the expanded input, left of the clear X (e.g. voice input). */
  endSlot?: ReactNode;
}

export function IconSearch({
  value,
  onChange,
  placeholder = "Search...",
  testId,
  className,
  endSlot,
}: IconSearchProps) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const open = expanded || value.length > 0;

  if (!open) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-9 w-9 shrink-0", className)}
            onClick={() => setExpanded(true)}
            aria-label={placeholder}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <Search className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{placeholder}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className={cn("relative flex-1 min-w-[120px] max-w-full", className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          if (!value) setExpanded(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onChange("");
            setExpanded(false);
          }
        }}
        placeholder={placeholder}
        className={cn("pl-8 h-9 text-sm", endSlot ? "pr-16" : "pr-8")}
        autoFocus
        data-testid={testId}
      />
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
        {endSlot}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => {
            onChange("");
            setExpanded(false);
          }}
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export interface IconSelectOption {
  value: string;
  label: ReactNode;
  icon?: LucideIcon;
  testId?: string;
}

interface IconSelectMenuProps {
  /** Trigger icon; defaults to the sort arrows. */
  icon?: LucideIcon;
  /** Accessible name + dropdown heading (e.g. "Sort by", "Space filter"). */
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: IconSelectOption[];
  testId?: string;
  /** Show a primary dot on the trigger when a non-default value is active. */
  active?: boolean;
  className?: string;
}

export function IconSelectMenu({
  icon: Icon = ArrowUpDown,
  label,
  value,
  onValueChange,
  options,
  testId,
  active = false,
  className,
}: IconSelectMenuProps) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-9 w-9 shrink-0 relative", className)}
              aria-label={label}
              data-testid={testId}
            >
              <Icon className="h-4 w-4" />
              {active && (
                <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-primary pointer-events-none" />
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-muted-foreground">{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          {options.map((opt) => {
            const OptIcon = opt.icon;
            return (
              <DropdownMenuRadioItem key={opt.value} value={opt.value} data-testid={opt.testId}>
                <span className="flex items-center gap-2 min-w-0">
                  {OptIcon && <OptIcon className="h-3 w-3 shrink-0" />}
                  <span className="truncate">{opt.label}</span>
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
